import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Overlay from '../../components/Overlay';
import ActionButton from '../../components/ActionButton';
import QtyStepper from '../../components/QtyStepper';
import { loadSizeToday, recordWelds, startWeldFlag, stopWeldFlag, countWelded } from '../../lib/welding';
import { STEPPER_MAX } from '../../lib/qtyStepper';
import {
  shelfLine, laterLine, fillPercent, flagWords, weldByLine, countsLine, reserveLine,
  previewLine, recordLabel, movedLine, refusalReason, refusalWords, todayEntryLine, recordedAnswer,
  RECOUNT_LABEL, NOBODY_ON_IT, NOTHING_TODAY, TYPE_FIRST, RECORDING,
  binFirstLine, COUNT_ASK, COUNT_HELP, SAVING, wasLine, countPreview, countLabel, countedAnswer, countOf,
  CHANGE_LABEL,
} from '../../lib/weldingWords';
import { WeldingReserveBody } from './WeldingReserves';

// #746 - A SIZE'S SHEET (gate 746, F2 to F4), Session 79.22b.6.3.1. Opened by
// tapping a size's bin on the Welding board; on a phone and a tablet it is a
// bottom sheet (Overlay, never position: fixed - locked rule 3). BUILT EXACTLY
// AS GROK DREW IT (746 D25): F2 is Tess Ivory on a size nobody is on, F4 is
// Nina Hart on a size two people are on, at the app's 90% cap with its list
// scrolling inside. Every sentence is weldingWords.js's, from the copy table.
//
// What is drawn where, because it moves with who is on the size:
//   nobody on it   - "No one is on it." under the reserve, and I'm on it under
//                    Record (F2);
//   someone else   - their names, Help on this and, when you are on another
//                    size, the sentence saying so, all under the reserve (F4);
//   you            - your own line with Stop in that place, as the board's bin
//                    draws it (F3, F5).
//
// CLEAR FLAG (746 D15), Session 79.22b.6.4, as Grok drew it and Colby approved
// it (docs/gates/drawings/2026-10-14_welding_746_clear_flag_grok.html, C1, C2,
// C4; R1 D2): for someone who may clear (canClearWeldFlag), each other person
// on the size has a line of their own - "Owen Pike is on it · since 7:40 am" -
// with a Clear flag on that line, so a manager ends exactly the forgotten flag.
// Anyone else keeps the single line F2 and F4 draw.
//
// THE COUNT SHEET (S6), Session 79.22b.6.4.1: Recount welded frames turns the
// sheet into S6 in place - the question with #791's helper line, Was N., the
// box starting at the current figure, the preview and Set it to N. A count
// answers on the page's bar with no Undo ("Count again to change it"). S7's
// button on a bin opens the sheet straight onto it.
//
// CHANGE ON THE RESERVE LINE (R2; 746 D29-D31), Session 79.22b.6.4b: for
// someone canSetWeldReserve() lets in (`mayReserve`), the reserve line carries
// Change, as the amended F4 draws it (`.rsetrow`), and Change turns the sheet
// into the one-size reserve sheet in place (WeldingReserves.js), as Recount
// welded frames turns it into the count. Anyone else keeps the plain line.

// S6 - THE COUNT SHEET'S CONTENTS (gate 746, S6; the copy table's "Count sheet"
// row). Module level (locked rule 2). Drawn as S6: the title and ×, the due
// line, the bar, who is on it, then the question - and nothing of the size
// sheet's own lines (weld-by, counts, reserve, today's entries).
function WeldingCountBody({ size, branchId, horizonDays, sizeFlags, myId, countReason, desk, onClose, onCounted, onChanged }) {
  // "the number starts at the current figure"
  const [qty, setQty] = useState(String(Math.max(Number(size.waitingShown) || 0, 0)));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const titleRef = useRef(null);
  useEffect(() => { titleRef.current?.focus(); }, []);

  const first = binFirstLine(size, horizonDays);
  const later = laterLine(size.later);
  const who = flagWords(sizeFlags, myId);
  const pct = fillPercent(size);
  const n = countOf(qty);

  async function save() {
    setError('');
    if (n == null) { setError(TYPE_FIRST); return; }
    setSaving(true);
    const r = await countWelded({ branchId, itemId: size.itemId, counted: n });
    setSaving(false);
    if (!r.ok) {
      // The sheet stays open with the typed number kept, and the board
      // re-reads (the "Sheet: busy, refused, answered" row).
      setError(refusalWords(r.error));
      onChanged?.();
      return;
    }
    onCounted?.({ text: countedAnswer(r.counted ?? n) });
  }

  return (
    <>
      <div className="wp-shtop">
        <h3 ref={titleRef} tabIndex={-1}>{size.name}</h3>
        <button type="button" className="wp-x" aria-label="Close" onClick={onClose}>×</button>
      </div>
      <p className="wp-ssub">
        {first}
        {first && later && <br />}
        {later && <span className="wp-later">{later}</span>}
      </p>
      <div className="wp-gauge" aria-hidden="true">{pct > 0 && <span style={{ width: `${pct}%` }} />}</div>
      {who && <p className="wp-flag">{who.text}</p>}
      <label className="wp-cask" htmlFor={`wp-count-${size.itemId}`}>{COUNT_ASK}</label>
      <p className="wp-chelp">{COUNT_HELP}</p>
      <p className="wp-was">{wasLine(size)}</p>
      <QtyStepper
        id={`wp-count-${size.itemId}`}
        className="wp-step"
        size="commit"
        min={0}
        boxStyle={desk ? { fontSize: 20 } : undefined}
        max={STEPPER_MAX}
        value={qty}
        onChange={v => { setQty(v); setError(''); }}
      />
      <p className="wp-preview" id={`wp-cpreview-${size.itemId}`}>{countPreview(size, qty)}</p>
      <ActionButton
        className="wp-btn pri"
        blockedReason={countReason}
        reasonStyle={REASON_BOX}
        busy={saving}
        busyLabel={SAVING}
        onClick={save}
        {...(!countReason && n == null ? { 'aria-disabled': true, 'aria-describedby': `wp-cpreview-${size.itemId}` } : {})}
      >
        {countLabel(qty)}
      </ActionButton>
      {error && <p className="wp-moved" role="alert">{error}</p>}
    </>
  );
}

// S5'S REASON, AS DRAWN (79.22b.6.4.1): the refusal printed once, in the
// attention box S5 draws (`.reason`) rather than ActionButton's plain line. Its
// colours stay ActionButton's own - resting #48545F, red once pressed - so a
// refused press is still answered on screen (#492, #503).
const REASON_BOX = Object.freeze({
  // A paragraph's own 14px below it, as the drawn `<p class="reason">` keeps.
  fontSize: 14, lineHeight: 1.35, marginTop: 8, marginBottom: 14, padding: '8px 12px', borderRadius: 8,
  background: 'var(--status-attention-bg)',
});

// How much shorter the sheet's tight spacing is (F4's `.sheet.fit`): the bottom
// padding 16 -> 8, How many's margins 12 + 8 -> 8 + 4, and the moved line's 8 -> 4.
function fitSaving(moved) { return 8 + 8 + (moved ? 4 : 0); }

// THE SHEET'S CONTENTS - shared with the desk's dialog at 79.22b.6.4.
export function WeldingSheetBody({
  size, branchId, branchName, horizonDays, coatShipDays, sizeFlags, myId, myFlagElsewhere,
  refusalKind, countRefusalKind, todayOverride, capped, desk, mayClear, clearingId, onClear,
  onClose, onRecorded, onChanged, startCount, mayReserve, startReserve,
}) {
  const [qty, setQty] = useState('');
  const [recording, setRecording] = useState(false);
  const [recordError, setRecordError] = useState('');
  const [flagBusy, setFlagBusy] = useState(false);
  const [flagError, setFlagError] = useState('');
  // S6: Recount welded frames turns this sheet into the count sheet; S7's bin
  // button opens it there directly.
  const [counting, setCounting] = useState(!!startCount);
  // R2: Change turns this sheet into the one-size reserve sheet. `startReserve`
  // opens it there directly - the measuring switch's alone, for R2's frame.
  const [reserving, setReserving] = useState(!!startReserve && !!mayReserve);
  // Clear flag's answer, in the sheet itself: the page's answer bar sits under
  // the open sheet's backdrop (Grok's comparison, finding 3).
  const [clearNote, setClearNote] = useState('');
  const [today, setToday] = useState(null);   // null while reading
  const titleRef = useRef(null);

  // Focus moves to the sheet's title (F3).
  useEffect(() => { titleRef.current?.focus(); }, []);

  // Today's entries (746 D20), read when the sheet opens and again whenever the
  // size's welded, waiting moves - a record or an Undo by anybody, arriving live.
  const readToday = useCallback(async () => {
    if (todayOverride) { setToday({ ok: true, entries: todayOverride }); return; }
    setToday(await loadSizeToday(branchId, size.itemId));
  }, [branchId, size.itemId, todayOverride]);
  useEffect(() => { readToday(); }, [readToday, size.weldedWaiting]);

  const mine = (sizeFlags || []).find(f => myId != null && String(f.user_id) === String(myId)) || null;
  const others = (sizeFlags || []).filter(f => f !== mine);
  const reason = refusalReason(refusalKind, branchName);
  const countReason = refusalReason(countRefusalKind, branchName);
  const n = parseInt(qty, 10);
  const empty = !(n > 0);
  const due = binFirstLine(size, horizonDays);
  const shelf = shelfLine(size.shelf);
  const later = laterLine(size.later);
  const weldBy = weldByLine(coatShipDays);
  const pct = fillPercent(size);
  const movedText = myFlagElsewhere && !mine ? movedLine(myFlagElsewhere, others.length > 0) : '';
  // ONE REASON, PRINTED ONCE. Someone who may not record is refused by Record,
  // the flag button and usually Recount for the same reason; three copies of a
  // two-line sentence in a row is noise (found on screen as Owen Pike,
  // 79.22b.6.3.1). The first blocked button prints it; the others still block,
  // still announce and still answer a press (ActionButton's reasonAlreadyShown).
  // Help on this sits above Record (F4), so when it is drawn it prints first.
  const helpFirst = !mine && others.length > 0;

  async function record() {
    setRecordError('');
    if (empty) { setRecordError(TYPE_FIRST); return; }
    setRecording(true);
    const r = await recordWelds({ branchId, itemId: size.itemId, qty: n });
    setRecording(false);
    if (!r.ok) {
      // The sheet stays open, the number is kept, and the board re-reads before
      // the button can be pressed again (X2: never record twice, never claim
      // what did not happen).
      setRecordError(refusalWords(r.error));
      onChanged?.();
      readToday();
      return;
    }
    onRecorded?.({ text: recordedAnswer(r.qty ?? n, size.name), recordId: r.recordId });
  }

  async function flag(start) {
    setFlagError('');
    setFlagBusy(true);
    const r = start ? await startWeldFlag({ branchId, itemId: size.itemId }) : await stopWeldFlag();
    setFlagBusy(false);
    if (!r.ok) { setFlagError(refusalWords(r.error)); return; }
    // The line itself is the answer (F3): no bar.
    onChanged?.();
  }

  const flagButton = label => (
    <>
      <ActionButton className="wp-btn" blockedReason={reason} reasonAlreadyShown={!helpFirst} reasonStyle={helpFirst ? REASON_BOX : undefined} busy={flagBusy}
        onClick={() => flag(true)}>{label}</ActionButton>
      {movedText && <p className="wp-moved">{movedText}</p>}
      {flagError && <p className="wp-moved" role="alert">{flagError}</p>}
    </>
  );

  if (reserving) {
    // On the desk, the size's window in R4's style (`.reserve`; section 4's box).
    return (
      <div className={`wp-sheet${desk ? ' desk reserve' : ''}`}>
        {!desk && <div className="wp-grab" aria-hidden="true" />}
        <WeldingReserveBody size={size} branchId={branchId} desk={desk} onClose={onClose}
          onChanged={onChanged} onSaved={a => onRecorded?.(a)} />
      </div>
    );
  }

  if (counting) {
    return (
      <div className={`wp-sheet count${capped ? ' fit' : ''}${desk ? ' desk' : ''}`}>
        {!desk && <div className="wp-grab" aria-hidden="true" />}
        <WeldingCountBody
          size={size} branchId={branchId} horizonDays={horizonDays} sizeFlags={sizeFlags} myId={myId}
          countReason={countReason} desk={desk} onClose={onClose} onChanged={onChanged}
          onCounted={a => onRecorded?.(a)}
        />
      </div>
    );
  }

  return (
    // S5's tighter spacing (`.sheet.cant`) for someone who may not record here.
    <div className={`wp-sheet${capped ? ' fit' : ''}${desk ? ' desk' : ''}${reason ? ' cant' : ''}`}>
      {!desk && <div className="wp-grab" aria-hidden="true" />}
      <div className="wp-shtop">
        <h3 ref={titleRef} tabIndex={-1}>{size.name}</h3>
        <button type="button" className="wp-x" aria-label="Close" onClick={onClose}>×</button>
      </div>
      <p className="wp-ssub">
        {due}
        {shelf && <>{due && <br />}{shelf}</>}
        {later && <>{(due || shelf) && <br />}<span className="wp-later">{later}</span></>}
      </p>
      {weldBy && <p className="wp-weldby">{weldBy}</p>}
      <div className="wp-gauge" aria-hidden="true">{pct > 0 && <span style={{ width: `${pct}%` }} />}</div>
      <p className="wp-counts">{countsLine(size)}</p>
      {mayReserve ? (
        <div className="wp-rsetrow">
          <p>{reserveLine(size)}</p>
          <button type="button" className="wp-btn wp-btn40" onClick={() => setReserving(true)}>{CHANGE_LABEL}</button>
        </div>
      ) : <p className="wp-rset">{reserveLine(size)}</p>}

      {mine && (
        <div className="wp-own">
          <span>{flagWords(mayClear ? [mine] : sizeFlags, myId).text}</span>
          <ActionButton className="wp-btn stop" busy={flagBusy} onClick={() => flag(false)}>Stop</ActionButton>
        </div>
      )}
      {mine && flagError && <p className="wp-moved" role="alert">{flagError}</p>}
      {mayClear && others.map(f => (
        <div key={f.id} className="wp-flagown">
          <span>{flagWords([f], myId).text}</span>
          <ActionButton className="wp-btn" aria-label={`Clear ${f.user_name || 'this'}'s flag`}
            busy={String(clearingId) === String(f.id)}
            onClick={async () => { setClearNote(''); setClearNote((await onClear?.(f)) || ''); }}>Clear flag</ActionButton>
        </div>
      ))}
      {clearNote && <p className="wp-moved" role="status">{clearNote}</p>}
      {!mine && others.length > 0 && (
        <>
          {!mayClear && <p className="wp-flag">{flagWords(others, myId).text}</p>}
          {flagButton('Help on this')}
        </>
      )}
      {!mine && !others.length && <p className="wp-ssub">{NOBODY_ON_IT}</p>}

      <label className="wp-ask" htmlFor={`wp-qty-${size.itemId}`}>How many</label>
      <QtyStepper
        id={`wp-qty-${size.itemId}`}
        className="wp-step"
        size="commit"
        min={1}
        boxStyle={desk ? { fontSize: 20 } : undefined}
        max={STEPPER_MAX}
        value={qty}
        onChange={v => { setQty(v); setRecordError(''); }}
      />
      <p className="wp-preview" id={`wp-preview-${size.itemId}`}>{previewLine(size, qty)}</p>
      <ActionButton
        className="wp-btn pri"
        blockedReason={reason}
        reasonAlreadyShown={helpFirst}
        reasonStyle={helpFirst ? undefined : REASON_BOX}
        busy={recording}
        busyLabel={RECORDING}
        onClick={record}
        {...(!reason && empty ? { 'aria-disabled': true, 'aria-describedby': `wp-preview-${size.itemId}` } : {})}
      >
        {recordLabel(qty)}
      </ActionButton>
      {recordError && <p className="wp-moved" role="alert">{recordError}</p>}
      {!mine && !others.length && flagButton("I'm on it")}
      <ActionButton className="wp-btn" blockedReason={countReason} reasonAlreadyShown={!!countReason && countReason === reason}
        reasonStyle={countReason === reason ? undefined : REASON_BOX}
        onClick={() => setCounting(true)}>{RECOUNT_LABEL}</ActionButton>

      <div className="wp-daylog">
        <p className="wp-daylabel">Today</p>
        {today == null && <p className="wp-daynone">Loading today's entries.</p>}
        {today && !today.ok && <p className="wp-daynone" role="alert">Today's entries could not be loaded.</p>}
        {today?.ok && !today.entries.length && <p className="wp-daynone">{NOTHING_TODAY}</p>}
        {today?.ok && today.entries.length > 0 && (
          <ul>{today.entries.map(e => <li key={`${e.kind}-${e.id}`}>{todayEntryLine(e)}</li>)}</ul>
        )}
      </div>
    </div>
  );
}

// THE PHONE AND TABLET SHEET: the contents in Overlay's bottom sheet, which
// caps at 90% of the screen and scrolls inside (F4 · scrolled). Grok's sheet
// tightens its spacing when it reaches the cap (`.sheet.fit`), so this measures
// whether the contents, at their ordinary spacing, would be taller than the
// cap - adding back what the tight spacing saved, so the choice cannot flicker.
export default function WeldingSizeSheet(props) {
  const { onClose, size, myFlagElsewhere } = props;
  const panelRef = useRef(null);
  const [capped, setCapped] = useState(false);
  const moved = !!myFlagElsewhere;

  useLayoutEffect(() => {
    const body = panelRef.current;
    const panel = body?.parentElement;
    if (!body || !panel || typeof ResizeObserver === 'undefined') return undefined;
    const decide = () => {
      const natural = panel.scrollHeight + (capped ? fitSaving(moved) : 0);
      const next = natural > panel.clientHeight + 1;
      if (next !== capped) setCapped(next);
    };
    decide();
    const ro = new ResizeObserver(decide);
    ro.observe(body.firstChild || body);
    return () => ro.disconnect();
  }, [capped, moved, size]);

  return (
    <Overlay
      variant="sheet"
      onClose={onClose}
      panelStyle={{ borderTop: 'none', borderLeft: 'none', borderRight: 'none', boxShadow: 'var(--shadow-md)' }}
    >
      <div ref={panelRef} className="wp-sheetwrap">
        <WeldingSheetBody {...props} capped={capped} />
      </div>
    </Overlay>
  );
}

// THE DESK'S WINDOW FOR A SIZE (F3: "Desk Record ... Opens the size's dialog,
// the same contents as the phone sheet"; F4: "The size's sheet becomes a
// dialog"), Session 79.22b.6.4: the same WeldingSheetBody in Overlay's centred
// modal, which caps at 90% of the window and scrolls inside.
export function WeldingSizeDialog(props) {
  const { onClose } = props;
  return (
    <Overlay variant="modal" onClose={onClose} maxWidth={520}
      panelStyle={{ border: 'none', boxShadow: 'var(--shadow-md)' }}>
      <WeldingSheetBody {...props} desk capped={false} />
    </Overlay>
  );
}
